import { useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import { DashboardLayout } from "../../../../../components/layouts/DashboardLayout";
import { Header } from "../../../../../components/Header";
import {
    FaNetworkWired,
    FaCopy,
    FaClipboardCheck,
    FaServer,
    FaWifi,
    FaPlus,
    FaMinus,
    FaEthernet,
} from "react-icons/fa6";
import { FaShieldAlt } from "react-icons/fa";

export const MikrotikConfigGenerator = () => {
    const [openSidebar, setOpenSidebar] = useState(true);
    const [copied, setCopied] = useState(false);

    // --- FORM STATE ---
    const [config, setConfig] = useState({
        routerName: "MikroTik-Danela",
        portCount: 5, // Default 5 port
        ports: [], // Akan di-generate otomatis: [{ id: 1, role: 'lan' }, ...]
        lanIp: "192.168.1.1/24",
        dhcpStart: "192.168.1.100",
        dhcpEnd: "192.168.1.254",

        // VLAN Settings (Ubah menjadi Array)
        vlans: [
            {
                id: Date.now(), // Unique ID untuk React Key
                name: "vlan-isp",
                vlanId: 10,
                parent: "ether1",
                type: "wan", // 'wan' atau 'lan'
                ip: "192.168.10.1/24",
                dhcpStart: "192.168.10.100",
                dhcpEnd: "192.168.10.254"
            }
        ],
    });

    // --- EFFECTS ---

    // Generate Port List saat portCount berubah
    useEffect(() => {
        generatePorts(config.portCount);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [config.portCount]);

    // Generate Ports Logic
    const generatePorts = (count) => {
        const newPorts = [];
        for (let i = 1; i <= count; i++) {
            const role = i === 1 ? "wan" : "lan";
            newPorts.push({
                id: i,
                name: `ether${i}`,
                role: role,
            });
        }

        setConfig((prev) => ({
            ...prev,
            ports: newPorts,
            // Reset parent VLAN jika ether1 hilang (opsional, untuk safety)
            vlans: prev.vlans.map(v => ({
                ...v,
                parent: (count >= 1 && v.parent === 'ether1') ? 'ether1' : (count >= 1 ? 'ether1' : '') 
            }))
        }));
    };

    // --- HANDLERS ---

    const handlePortRoleChange = (id, newRole) => {
        if (newRole === "wan") {
            const updatedPorts = config.ports.map((p) => ({
                ...p,
                role: p.id === id ? "wan" : "lan",
            }));
            setConfig((prev) => ({ ...prev, ports: updatedPorts }));
        } else {
            const updatedPorts = config.ports.map((p) => ({
                ...p,
                role: p.id === id ? newRole : p.role,
            }));
            setConfig((prev) => ({ ...prev, ports: updatedPorts }));
        }
    };

    // Handler untuk Tambah VLAN
    const handleAddVlan = () => {
        const newId = Date.now();
        setConfig((prev) => ({
            ...prev,
            vlans: [
                ...prev.vlans,
                {
                    id: newId,
                    name: `vlan-new-${prev.vlans.length + 1}`,
                    vlanId: 10 + prev.vlans.length,
                    parent: prev.ports.find(p => p.role === 'wan')?.name || 'ether1',
                    type: 'lan',
                    ip: `192.168.${10 + prev.vlans.length}.1/24`,
                    dhcpStart: `192.168.${10 + prev.vlans.length}.100`,
                    dhcpEnd: `192.168.${10 + prev.vlans.length}.254`
                }
            ]
        }));
    };

    // Handler untuk Hapus VLAN
    const handleRemoveVlan = (id) => {
        setConfig((prev) => ({
            ...prev,
            vlans: prev.vlans.filter((v) => v.id !== id)
        }));
    };

    // Handler untuk Update data VLAN spesifik
    const handleVlanChange = (id, field, value) => {
        setConfig((prev) => ({
            ...prev,
            vlans: prev.vlans.map((v) => {
                if (v.id === id) {
                    return { ...v, [field]: value };
                }
                return v;
            })
        }));
    };

    const generateScript = () => {
        const wanPort = config.ports.find((p) => p.role === "wan");
        const lanPorts = config.ports.filter((p) => p.role === "lan");

        if (!wanPort) return "# ERROR: Silakan pilih minimal 1 port sebagai WAN.";

        // Script Base
        let script = `# ============================================================
# MIKROTIK AUTO CONFIGURATION
# Generated by: DANELA NET SYSTEM
# Date: ${new Date().toLocaleString()}
# ============================================================

# 1. System Identity & Default Config
/system identity set name="${config.routerName}"
/system clock set time-zone-name=Asia/Makassar

# 2. Interface Setup (Renaming & Bridging)
/interface bridge remove [find default=yes]
/interface bridge add name=bridge-local protocol-mode=rstp
`;

        lanPorts.forEach((port) => {
            script += `/interface bridge port add bridge=bridge-local interface=${port.name} pvid=1\n`;
        });

        script += `
# 3. IP Addressing Configuration (Main LAN)
/ip address add address=${config.lanIp} interface=bridge-local comment="LAN Gateway"
/ip dhcp-client add interface=${wanPort.name} disabled=no comment="WAN - ${wanPort.name}"

`;

        // --- MULTI VLAN LOGIC ---
        if (config.vlans.length > 0) {
            script += `# ============================================================
# 4. VLAN CONFIGURATION (${config.vlans.length} VLAN Detected)
# ============================================================\n\n`;

            config.vlans.forEach((vlan, index) => {
                script += `# --- VLAN ${index + 1}: ${vlan.name} (ID: ${vlan.vlanId}) ---
# Type: ${vlan.type.toUpperCase()}

# A. Buat Interface VLAN
/interface vlan add name=${vlan.name} vlan-id=${vlan.vlanId} interface=${vlan.parent}

# B. Buat Bridge Khusus VLAN ini (untuk isolasi jaringan)
/interface bridge add name=bridge-${vlan.name}

# C. Masukkan Interface VLAN ke Bridge-nya
/interface bridge port add bridge=bridge-${vlan.name} interface=${vlan.name}

`;

                if (vlan.type === 'wan') {
                    script += `# D. Setup DHCP Client (VLAN sebagai WAN)
/ip dhcp-client add interface=bridge-${vlan.name} disabled=no use-peer-dns=yes comment="WAN VLAN ${vlan.name}"

`;
                } else {
                    // Jika LAN
                    const gatewayIp = vlan.ip.split('/')[0];
                    script += `# D. Setup IP Address & DHCP Server (VLAN sebagai LAN)
/ip address add address=${vlan.ip} interface=bridge-${vlan.name} comment="Gateway ${vlan.name}"

/ip pool add name=pool-${vlan.name} ranges=${vlan.dhcpStart}-${vlan.dhcpEnd}

/ip dhcp-server
add name=dhcp-${vlan.name} interface=bridge-${vlan.name} address-pool=pool-${vlan.name} disabled=no \
    lease-time=3d authoritative=yes

/ip dhcp-server network
add address=${gatewayIp} gateway=${gatewayIp} \
    dns-server=8.8.8.8,1.1.1.1 comment="Network ${vlan.name}"

`;
                }
            });
        }

        // DHCP Server Setup (Main LAN)
        script += `# 5. DHCP Server Setup (Main LAN Bridge)
/ip pool add name=dhcp_pool1 ranges=${config.dhcpStart}-${config.dhcpEnd}
/ip dhcp-server
add name=dhcp1 interface=bridge-local address-pool=dhcp_pool1 disabled=no \
    lease-time=3d authoritative=yes
/ip dhcp-server network
add address=${config.lanIp.split('/')[0]} gateway=${config.lanIp.split('/')[0]} \
    dns-server=8.8.8.8,1.1.1.1 comment="Main LAN Network"

`;

        // NAT / Masquerade
        // Kita buat NAT umum keluar via WAN Utama, dan NAT spesifik jika ada VLAN LAN
        script += `# 6. NAT / Masquerade
# NAT untuk Main LAN keluar via WAN Utama (${wanPort.name})
/ip firewall nat add chain=srcnat out-interface=${wanPort.name} action=masquerade comment="NAT Main LAN"

`;

        // Tambahkan NAT untuk VLAN LAN jika ada
        const lanVlans = config.vlans.filter(v => v.type === 'lan');
        lanVlans.forEach(vlan => {
            script += `# NAT untuk VLAN ${vlan.name} keluar via WAN Utama
/ip firewall nat add chain=srcnat out-interface=${wanPort.name} action=masquerade src-address=${vlan.ip} comment="NAT VLAN ${vlan.name}"
`;
        });

        script += `# 7. Firewall Basic Security
/ip firewall filter
add chain=input connection-state=established,related action=accept comment="Allow Established"
add chain=input connection-state=invalid action=drop comment="Drop Invalid"
add chain=input protocol=icmp action=accept comment="Allow Ping"
add chain=input src-address=${config.lanIp.split('/')[0]} action=accept comment="Allow From LAN"
add chain=input action=drop comment="Drop Everything Else"

# 8. Finish
/log info message="Configuration Complete: ${config.routerName}"
`;
        return script;
    };

    const scriptOutput = generateScript();

    const handleCopy = () => {
        navigator.clipboard.writeText(scriptOutput);
        setCopied(true);
        setTimeout(() => setCopied(false), 3000);
    };

    const sectionStyle = {
        background: "rgba(15, 23, 42, 0.5)",
        border: "1px solid rgba(51, 65, 85, 0.3)",
        backdropFilter: "blur(12px)",
    };

    const terminalStyle = {
        background: "#0f172a",
        border: "1px solid #334155",
        fontFamily: "'Fira Code', 'Courier New', monospace",
    };

    return (
        <>
            <Helmet>
                <title>Mikrotik Config Generator | Danela Net</title>
            </Helmet>

            <DashboardLayout showSidebar={openSidebar} setShowSidebar={setOpenSidebar}>
                <Header showToggleSidebar={false} toggleSidebar={() => setOpenSidebar(!openSidebar)}>
                    <div className="flex flex-col">
                        <h2 className="text-lg md:text-xl font-display font-bold text-gray-100 tracking-tight">
                            Mikrotik Config Generator
                        </h2>
                        <p className="text-xs text-slate-500 hidden md:block mt-0.5">
                            Generate script otomatis untuk setup dasar Mikrotik.
                        </p>
                    </div>
                </Header>

                <div className="p-4 lg:p-6 space-y-6">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

                        {/* --- KOLOM KIRI: FORM INPUT --- */}
                        <div className="space-y-6">

                            {/* 1. Basic Info */}
                            <div className="p-5 rounded-2xl" style={sectionStyle}>
                                <h3 className="font-bold text-gray-100 mb-4 flex items-center gap-2 text-sm">
                                    <FaServer className="text-cyan-400" /> Identitas Dasar
                                </h3>
                                <div className="grid grid-cols-1 gap-4">
                                    <div>
                                        <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">Nama Router</label>
                                        <input
                                            type="text"
                                            value={config.routerName}
                                            onChange={(e) => setConfig({ ...config, routerName: e.target.value })}
                                            className="w-full bg-slate-800/60 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-200 focus:border-cyan-500 outline-none"
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* 2. Port Configuration */}
                            <div className="p-5 rounded-2xl" style={sectionStyle}>
                                <div className="flex justify-between items-center mb-4">
                                    <h3 className="font-bold text-gray-100 flex items-center gap-2 text-sm">
                                        <FaEthernet className="text-purple-400" /> Setup Port Fisik
                                    </h3>

                                    <div className="flex items-center gap-2 bg-slate-800 rounded-lg p-1 border border-slate-700">
                                        <button
                                            onClick={() => config.portCount > 2 && setConfig({ ...config, portCount: config.portCount - 1 })}
                                            className="w-6 h-6 flex items-center justify-center text-slate-400 hover:text-white hover:bg-slate-700 rounded"
                                        >
                                            <FaMinus size={10} />
                                        </button>
                                        <span className="text-xs font-bold text-slate-200 w-4 text-center">{config.portCount}</span>
                                        <button
                                            onClick={() => setConfig({ ...config, portCount: config.portCount + 1 })}
                                            className="w-6 h-6 flex items-center justify-center text-slate-400 hover:text-white hover:bg-slate-700 rounded"
                                        >
                                            <FaPlus size={10} />
                                        </button>
                                    </div>
                                </div>

                                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                                    {config.ports.map((port) => (
                                        <div
                                            key={port.id}
                                            onClick={() => handlePortRoleChange(port.id, port.role === 'wan' ? 'lan' : 'wan')}
                                            className={`
                                        cursor-pointer p-3 rounded-xl border text-center transition-all duration-200
                                        ${port.role === 'wan'
                                                    ? 'bg-rose-500/10 border-rose-500 text-rose-400'
                                                    : port.role === 'lan'
                                                        ? 'bg-cyan-500/10 border-cyan-500 text-cyan-400'
                                                        : 'bg-slate-800/30 border-slate-700 text-slate-500 hover:bg-slate-700/50'
                                                }
                                    `}
                                        >
                                            <div className="text-xs font-bold mb-1">{port.name}</div>
                                            <div className="text-[10px] uppercase opacity-80">
                                                {port.role === 'wan' ? 'INTERNET (WAN)' : port.role === 'lan' ? 'LAN (CLIENT)' : 'UNUSED'}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                                <p className="text-[10px] text-slate-500 mt-3 flex items-center gap-1">
                                    <FaNetworkWired size={8} /> Klik port untuk mengganti fungsi (WAN/LAN).
                                </p>
                            </div>

                            {/* 3. LAN Settings */}
                            <div className="p-5 rounded-2xl" style={sectionStyle}>
                                <h3 className="font-bold text-gray-100 mb-4 flex items-center gap-2 text-sm">
                                    <FaWifi className="text-blue-400" /> Jaringan Utama (LAN)
                                </h3>
                                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                                    <div className="col-span-1">
                                        <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">IP Gateway</label>
                                        <input
                                            type="text"
                                            value={config.lanIp}
                                            onChange={(e) => setConfig({ ...config, lanIp: e.target.value })}
                                            className="w-full bg-slate-800/60 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-200 focus:border-cyan-500 outline-none"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">DHCP Start</label>
                                        <input
                                            type="text"
                                            value={config.dhcpStart}
                                            onChange={(e) => setConfig({ ...config, dhcpStart: e.target.value })}
                                            className="w-full bg-slate-800/60 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-200 focus:border-cyan-500 outline-none"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">DHCP End</label>
                                        <input
                                            type="text"
                                            value={config.dhcpEnd}
                                            onChange={(e) => setConfig({ ...config, dhcpEnd: e.target.value })}
                                            className="w-full bg-slate-800/60 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-200 focus:border-cyan-500 outline-none"
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* 4. VLAN Settings (Multi) */}
                            <div className="p-5 rounded-2xl" style={sectionStyle}>
                                <div className="flex items-center justify-between mb-4">
                                    <h3 className="font-bold text-gray-100 flex items-center gap-2 text-sm">
                                        <FaShieldAlt className="text-amber-400" /> Setup VLAN Tambahan
                                    </h3>
                                    <button
                                        onClick={handleAddVlan}
                                        className="flex items-center gap-1 px-3 py-1.5 bg-cyan-600 hover:bg-cyan-500 text-white text-[10px] font-bold rounded-lg transition-colors"
                                    >
                                        <FaPlus size={8} /> Tambah VLAN
                                    </button>
                                </div>

                                <div className="space-y-4">
                                    {config.vlans.map((vlan, index) => (
                                        <div key={vlan.id} className="relative p-4 rounded-xl border bg-slate-800/40 border-slate-700 animate-fadeIn">
                                            
                                            {/* Header VLAN Item */}
                                            <div className="flex justify-between items-start mb-3">
                                                <div className="flex items-center gap-2">
                                                    <span className="text-xs font-bold text-slate-400">VLAN #{index + 1}</span>
                                                    <div className="h-4 w-[1px] bg-slate-600"></div>
                                                    <input 
                                                        type="text" 
                                                        value={vlan.name}
                                                        onChange={(e) => handleVlanChange(vlan.id, 'name', e.target.value)}
                                                        className="bg-transparent border-b border-slate-600 text-xs text-cyan-400 font-mono focus:outline-none focus:border-cyan-400 w-24"
                                                    />
                                                </div>
                                                <button
                                                    onClick={() => handleRemoveVlan(vlan.id)}
                                                    className="text-slate-500 hover:text-rose-400 transition-colors"
                                                    title="Hapus VLAN"
                                                >
                                                    <FaMinus size={12} />
                                                </button>
                                            </div>

                                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-3">
                                                <div>
                                                    <label className="block text-[9px] font-bold text-slate-500 uppercase mb-1">VLAN ID</label>
                                                    <input
                                                        type="number"
                                                        value={vlan.vlanId}
                                                        onChange={(e) => handleVlanChange(vlan.id, 'vlanId', e.target.value)}
                                                        className="w-full bg-slate-700/50 border border-slate-600 rounded px-2 py-1 text-xs text-white"
                                                    />
                                                </div>
                                                <div className="col-span-2">
                                                    <label className="block text-[9px] font-bold text-slate-500 uppercase mb-1">Parent Interface</label>
                                                    <select
                                                        value={vlan.parent}
                                                        onChange={(e) => handleVlanChange(vlan.id, 'parent', e.target.value)}
                                                        className="w-full bg-slate-700/50 border border-slate-600 rounded px-2 py-1 text-xs text-white"
                                                    >
                                                        {config.ports.map(p => (
                                                            <option key={p.id} value={p.name}>{p.name} ({p.role.toUpperCase()})</option>
                                                        ))}
                                                    </select>
                                                </div>
                                                <div>
                                                    <label className="block text-[9px] font-bold text-slate-500 uppercase mb-1">Tipe</label>
                                                    <select
                                                        value={vlan.type}
                                                        onChange={(e) => handleVlanChange(vlan.id, 'type', e.target.value)}
                                                        className={`w-full border rounded px-2 py-1 text-xs text-white font-bold ${
                                                            vlan.type === 'wan' 
                                                            ? 'bg-rose-500/20 border-rose-500 text-rose-400' 
                                                            : 'bg-cyan-500/20 border-cyan-500 text-cyan-400'
                                                        }`}
                                                    >
                                                        <option value="wan">WAN (ISP)</option>
                                                        <option value="lan">LAN (Client)</option>
                                                    </select>
                                                </div>
                                            </div>

                                            {/* Opsional: IP Settings jika Type LAN */}
                                            {vlan.type === 'lan' && (
                                                <div className="grid grid-cols-3 gap-2 pt-2 border-t border-slate-700/50">
                                                    <div className="col-span-3">
                                                        <label className="block text-[9px] font-bold text-slate-500 uppercase mb-1">IP Gateway</label>
                                                        <input
                                                            type="text"
                                                            value={vlan.ip}
                                                            onChange={(e) => handleVlanChange(vlan.id, 'ip', e.target.value)}
                                                            className="w-full bg-slate-700/50 border border-slate-600 rounded px-2 py-1 text-xs text-white"
                                                        />
                                                    </div>
                                                    <div>
                                                        <label className="block text-[9px] font-bold text-slate-500 uppercase mb-1">DHCP Start</label>
                                                        <input
                                                            type="text"
                                                            value={vlan.dhcpStart}
                                                            onChange={(e) => handleVlanChange(vlan.id, 'dhcpStart', e.target.value)}
                                                            className="w-full bg-slate-700/50 border border-slate-600 rounded px-2 py-1 text-xs text-white"
                                                        />
                                                    </div>
                                                    <div className="col-span-2">
                                                        <label className="block text-[9px] font-bold text-slate-500 uppercase mb-1">DHCP End</label>
                                                        <input
                                                            type="text"
                                                            value={vlan.dhcpEnd}
                                                            onChange={(e) => handleVlanChange(vlan.id, 'dhcpEnd', e.target.value)}
                                                            className="w-full bg-slate-700/50 border border-slate-600 rounded px-2 py-1 text-xs text-white"
                                                        />
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                    
                                    {config.vlans.length === 0 && (
                                        <div className="text-center py-6 text-slate-500 text-xs italic border-2 border-dashed border-slate-700 rounded-xl">
                                            Belum ada VLAN tambahan. Klik "Tambah VLAN".
                                        </div>
                                    )}
                                </div>
                            </div>

                        </div>

                        {/* --- KOLOM KANAN: SCRIPT OUTPUT --- */}
                        <div className="flex flex-col h-full">
                            <div className="rounded-2xl overflow-hidden flex flex-col h-full border border-slate-700 bg-slate-900 shadow-2xl">
                                {/* Terminal Header */}
                                <div className="flex items-center justify-between px-4 py-3 bg-slate-950 border-b border-slate-800">
                                    <div className="flex items-center gap-2">
                                        <div className="flex gap-1.5">
                                            <div className="w-2.5 h-2.5 rounded-full bg-red-500"></div>
                                            <div className="w-2.5 h-2.5 rounded-full bg-yellow-500"></div>
                                            <div className="w-2.5 h-2.5 rounded-full bg-green-500"></div>
                                        </div>
                                        <span className="text-xs font-mono text-slate-400 ml-2">new-terminal</span>
                                    </div>
                                    <button
                                        onClick={handleCopy}
                                        className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-[10px] font-bold transition-all border ${copied
                                                ? 'bg-emerald-600 text-white border-emerald-600'
                                                : 'bg-slate-800 text-slate-300 border-slate-600 hover:bg-slate-700'
                                            }`}
                                    >
                                        {copied ? <><FaClipboardCheck /> Copied!</> : <><FaCopy /> Salin Script</>}
                                    </button>
                                </div>

                                {/* Terminal Body */}
                                <div className="flex-1 overflow-y-auto p-4">
                                    <pre
                                        className="text-xs sm:text-sm text-emerald-400 font-mono leading-relaxed whitespace-pre-wrap"
                                        style={terminalStyle}
                                    >
                                        {scriptOutput}
                                    </pre>
                                </div>

                                {/* Terminal Footer / Instruction */}
                                <div className="p-3 bg-slate-900/50 border-t border-slate-800 text-[10px] text-slate-400 text-center">
                                    Paste script di atas ke <span className="text-cyan-400 font-bold">New Terminal</span> MikroTik Anda dan tekan Enter.
                                </div>
                            </div>
                        </div>

                    </div>
                </div>
            </DashboardLayout>
        </>
    );
};